import CommercialMaintenanceTemplate from './commercial-maintenance-template/commercial-maintenance-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';
import { Helmet } from 'react-helmet-async';

const heroImageSrc =
  '/static/images/commercial-maintenance/southbank-office-white-board-installation-01.webp';

const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
];

const commercialMaintenanceGalleryImages =
  Object.entries(imagesMetadata)
    .filter(
      ([src]) =>
        src.startsWith(
          '/static/images/commercial-maintenance/'
        ) &&
        !excludedImages.includes(src)
    )
    .map(([src, meta], index) => ({
      id: index + 1,
      src,
      alt: meta.alt,
      location: meta.location,
    }));

export default function CommercialMaintenance() {

  const servicesData = {

    title: "Commercial Maintenance Services Melbourne",

    subtitle:
      "Commercial maintenance and handyman services in Melbourne for offices, retail stores, gyms, warehouses, businesses and commercial properties. We handle general repairs, installations, fixtures, fittings and property maintenance.",

    metaTitle:
      "Commercial Handyman Melbourne | Business Property Maintenance",

    metaDesc:
      "Commercial handyman and property maintenance in Melbourne for offices, retail and warehouses. Book reliable repairs, installations and ongoing upkeep.",

    canonicalPath: "/commercial-maintenance",

    heroBackgroundImage:
      "/static/images/branding/commercial-maintenance-background-handyman-in-melbourne.webp",

    heroBackgroundPosition: "bottom right",

    solutionsTitle: "Our Commercial Maintenance Services",

    solutionsIntro:
      "Our commercial maintenance services help Melbourne businesses, property managers and commercial property owners take care of everyday repairs, installations and maintenance tasks that keep their premises functional and well maintained. From repairing doors and fixtures to installing dispensers, shelving and workplace fittings, we provide practical commercial handyman services for a wide range of properties.",

    solutionsList: [
      {
        emoji: "🔧",
        heading: "Commercial Repairs & Maintenance",
        text:
          "General repairs and maintenance for doors, walls, fixtures, fittings, hardware and everyday property issues.",
      },

      {
        emoji: "🛠️",
        heading: "Commercial Installations",
        text:
          "Installation of shelves, TVs, whiteboards, dispensers, fixtures, fittings and other commercial accessories.",
      },

      {
        emoji: "🏠",
        heading: "Real Estate & Property Maintenance",
        text:
          "Practical maintenance for rental and managed properties, including repairs, installations, fixtures and general upkeep.",
      },

      {
        emoji: "🏢",
        heading: "Business & Retail Maintenance",
        text:
          "Handyman services for offices, retail stores, gyms, warehouses and other commercial premises across Melbourne.",
      },

    ],


    /* =====================================================
       MAIN SERVICE IMAGE
    ===================================================== */

    serviceImage: {

      src: heroImageSrc,

      alt:
        heroMeta?.alt ||
        "Commercial handyman carrying out maintenance work inside a commercial property in Melbourne",

    },

    serviceImagePosition: "bottom right",


    /* =====================================================
       COMMERCIAL PROPERTY TYPES
    ===================================================== */

    propertyTypes: [

      {
        emoji: "🏢",

        heading:
          "Offices & Workplaces",

        text:
          "Commercial handyman and maintenance services for offices and workplaces, including general repairs, fixtures, fittings, shelving, doors and other workplace maintenance tasks.",
      },

      {
        emoji: "🏬",

        heading:
          "Retail Stores",

        text:
          "Maintenance and handyman services for retail stores, shops and commercial premises, including fixtures, fittings, repairs and practical installations.",
      },

      {
        emoji: "🏋️",

        heading:
          "Gyms & Fitness Facilities",

        text:
          "General commercial maintenance and handyman services for gyms and fitness facilities, including fixtures, fittings, dispensers, repairs and installations.",
      },

      {
        emoji: "📦",

        heading:
          "Warehouses",

        text:
          "Practical handyman and maintenance services for warehouses and commercial facilities, including general repairs, fixtures, fittings and property upkeep.",
      },

      {
        emoji: "👨‍💼",

        heading:
          "Property Managers",

        text:
          "Commercial maintenance support for property managers responsible for keeping commercial properties maintained, functional and ready for tenants.",
      },

      {
        emoji: "🏪",

        heading:
          "Small Businesses",

        text:
          "Flexible commercial handyman services for Melbourne businesses requiring assistance with repairs, installations and ongoing property maintenance.",
      },

    ],


    /* =====================================================
       EXTENDED SEO CONTENT
    ===================================================== */

    extendedDetails: [

      {
        title: "Repairs & Installations",
        body: "We handle practical jobs such as door and hardware repairs, fixture replacement, shelving and workplace fittings to keep your premises in good working order.",
      },

      {
        title: "Less Disruption",
        body: "We coordinate with you, work efficiently and aim to minimise disruption to staff, customers and normal business operations wherever practical.",
      },

      {
        title: "Multiple Jobs, One Visit",
        body: "If you have several repairs or installations to take care of, we can assess them together and complete multiple practical jobs in the same visit where possible.",
      },

      {
        title: "Flexible Support",
        body: "Book us for a one-off repair, a group of maintenance jobs or regular support for your commercial property.",
      },

    ],


    /* =====================================================
       RECENT COMMERCIAL WORKS
    ===================================================== */

    galleryImages:
      commercialMaintenanceGalleryImages,


    /* =====================================================
       FAQ
    ===================================================== */

    faqs: [

      {
        question:
          "What commercial maintenance services do you provide in Melbourne?",

        answer:
          "We provide commercial handyman and maintenance services including general repairs, installations, fixtures, fittings, hardware, shelving, dispensers and other practical maintenance tasks for commercial properties across Melbourne.",
      },

      {
        question:
          "Do you provide commercial handyman services for offices and businesses?",

        answer:
          "Yes. We provide general commercial handyman services for offices, workplaces, retail stores, gyms, warehouses, small businesses and other commercial properties.",
      },

      {
        question:
          "What types of commercial properties do you service?",

        answer:
          "We service a range of commercial properties including offices, workplaces, retail stores, gyms, fitness facilities, warehouses, small businesses and properties managed by property managers.",
      },

      {
        question:
          "Do you work with property managers and commercial landlords?",

        answer:
          "Yes. We can provide commercial handyman and maintenance support for property managers and businesses responsible for commercial properties and general property upkeep.",
      },

      {
        question:
          "Can you handle multiple commercial maintenance jobs in one visit?",

        answer:
          "Where practical, yes. If a property has several smaller repairs, installations or maintenance tasks, we can assess the work together and determine what can be completed as part of the same visit.",
      },

      {
        question:
          "Do you provide ongoing commercial property maintenance?",

        answer:
          "Yes. We can assist with ongoing maintenance as well as one-off commercial repair and handyman jobs, depending on the requirements of the property.",
      },

      {
        question:
          "Can you work around our business hours?",

        answer:
          "We can discuss suitable times for the work and, where practical, arrange maintenance to minimise disruption to staff, customers, tenants and normal business operations.",
      },

      {
        question:
          "Do you provide quotes for commercial maintenance work?",

        answer:
          "Yes. Send us details of the commercial maintenance work required, along with clear photos where possible, and we can assess the job and provide a quote.",
      },

    ],


    /* =====================================================
       FINAL CTA
    ===================================================== */

    ctaTitle:
      "Need Commercial Maintenance?",

    ctaText:
      "Send through details or clear photos of the commercial maintenance work required. We can assess your needs and provide a transparent quote for the work.",

  };


  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://handymaninmelbourne.au/commercial-maintenance#service",
    "name": "Commercial Maintenance Services in Melbourne",
    "serviceType": "Commercial Maintenance",
    "description": servicesData.metaDesc,
    "url": "https://handymaninmelbourne.au/commercial-maintenance",
    "image": "https://handymaninmelbourne.au" + heroImageSrc,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Handyman In Melbourne",
      "url": "https://handymaninmelbourne.au/"
    },
    "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <CommercialMaintenanceTemplate
        {...servicesData}
      />
    </>
  );
}
